/**
 * Org-level notification switches. Admin-only. Turning one off stops that
 * automated email for everyone (sits on top of each person's own preferences).
 */
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";

const AUDIENCE_LABEL: Record<string, string> = {
  claimant: "To claimants",
  manager: "To managers",
  payroll: "To payroll",
};
const AUDIENCE_ORDER = ["manager", "payroll", "claimant"];

export default function NotificationsAdmin() {
  const qc = useQueryClient();
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ["notification-settings"],
    queryFn: () => api.admin.listNotificationSettings(),
  });
  const save = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api.admin.setNotificationSetting(key, enabled),
    onSuccess: (row) => {
      qc.setQueryData<typeof settings>(["notification-settings"], (prev) =>
        prev?.map((s) => (s.notif_key === row.notif_key ? { ...s, ...row } : s)),
      );
    },
  });

  const grouped = (settings ?? []).reduce<Record<string, typeof settings>>((acc, s) => {
    (acc[s.audience] ??= []).push(s);
    return acc;
  }, {} as any);

  return (
    <div className="space-y-6 max-w-2xl">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Notifications</h1>
        <p className="text-slate-600 mt-1">
          Switch automated expense emails on or off for the whole organisation. Turning one off stops it
          for everyone — individual staff preferences still apply on top of these.
        </p>
      </div>

      {isLoading && <div className="text-slate-500">Loading…</div>}
      {error && <div className="text-red-600 text-sm">{(error as Error).message}</div>}

      {settings && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">
          {AUDIENCE_ORDER.filter((a) => grouped[a]?.length).map((audience) => (
            <div key={audience}>
              <h2 className="font-medium text-liaise-navy mb-3 text-sm uppercase tracking-wide">
                {AUDIENCE_LABEL[audience] ?? audience}
              </h2>
              <div className="space-y-3">
                {grouped[audience]!.map((s) => (
                  <Toggle
                    key={s.notif_key}
                    label={s.label}
                    desc={s.description ?? ""}
                    checked={s.enabled}
                    onChange={(v) => save.mutate({ key: s.notif_key, enabled: v })}
                  />
                ))}
              </div>
            </div>
          ))}

          {save.isPending && <div className="text-xs text-slate-500">Saving…</div>}
          {save.error && <div className="text-xs text-red-600">{(save.error as Error).message}</div>}
        </div>
      )}
    </div>
  );
}

function Toggle({ label, desc, checked, onChange }: { label: string; desc: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4" />
      <div>
        <div className="font-medium text-slate-800">{label}</div>
        {desc && <div className="text-sm text-slate-500 mt-0.5">{desc}</div>}
      </div>
    </label>
  );
}
